import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, useToast } from '../../design-system';
import { getFix } from '../../lib/geolocation';
import { useLiveRefresh, useRefreshNow } from '../../lib/useLiveRefresh';
import ManualHoursForm from './ManualHoursForm';
import DayTimeline from './DayTimeline';
import '../../org-admin/styles/Attendance.css';

// Why someone is stepping away. The gap that follows a session is typed by
// this, and only `official_work` counts as worked time — a bank run is work,
// lunch is not.
const STEP_AWAY = [
  { reason: 'break', label: 'Short break', hint: 'Not counted as work' },
  { reason: 'lunch', label: 'Lunch', hint: 'Not counted as work' },
  { reason: 'official_work', label: 'Out on office work', hint: 'Counted as work' },
  { reason: 'done', label: 'Done for the day', hint: 'Ends today' },
];

// The clock-in/out control and today's figure.
//
// Shared, so the employee page and the cockpit page show the same thing — and
// so there is exactly one place that decides what "clocked in" looks like.
//
// The elapsed figure ticks locally between polls: the server hands back a
// minute count and the moment the open session started, and this counts up from
// that rather than asking every second.

const pad = (n) => String(n).padStart(2, '0');
const hhmm = (mins) => `${Math.floor(Math.max(0, mins) / 60)}h ${pad(Math.max(0, Math.round(mins)) % 60)}m`;
const clockTime = (d) => (d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');

function AttendanceClock({ onChange, compact = false }) {
  const toast = useToast();
  // The other half of the same boundary the day prompt sits on: this widget is
  // on a page, that dialog is in the layout, and neither can reach the other
  // through props. `onChange` still drives the month and board beside it — it
  // is not replaced, only joined.
  const revision = useLiveRefresh();
  const bump = useRefreshNow();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stepAwayOpen, setStepAwayOpen] = useState(false);
  const [, forceTick] = useState(0);
  const timer = useRef(null);

  const load = useCallback(() => {
    apiClient
      .get('/api/attendance/today')
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, []);

  useEffect(() => {
    load();
    // A slow poll keeps the figure honest if the person clocked in on another
    // device; the local tick below does the second-by-second work. The
    // revision is what makes a change on THIS device immediate rather than
    // waiting out the poll.
    const poll = setInterval(load, 60000);
    return () => clearInterval(poll);
  }, [load, revision]);

  useEffect(() => {
    if (!state?.isClockedIn) { clearInterval(timer.current); return undefined; }
    timer.current = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(timer.current);
  }, [state?.isClockedIn]);

  const clockIn = async () => {
    setBusy(true);
    try {
      // Only ask when the firm has drawn a fence — otherwise the prompt buys
      // nothing and just trains people to dismiss it. getFix never rejects: a
      // refusal or a timeout resolves to null and the day is simply remote.
      const location = state?.geofenceEnabled ? await getFix() : null;

      const { data } = await apiClient.post('/api/attendance/clock-in', { location });
      // ⚠️ A BUMP INSTEAD OF A SECOND GET, not as well as one. The revision
      // effect above re-reads this widget, so fetching here too would ask for
      // today's record twice on every clock-in.
      onChange?.();
      bump();
      toast.success(
        data.workMode === 'office'
          ? `Clocked in at ${data.officeName || 'the office'}`
          : 'Clocked in — working remotely'
      );
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not update attendance');
    } finally {
      setBusy(false);
    }
  };

  const clockOut = async (endReason) => {
    setBusy(true);
    setStepAwayOpen(false);
    try {
      await apiClient.post('/api/attendance/clock-out', { endReason });
      onChange?.();
      bump();
      toast.success(
        endReason === 'done'
          ? 'Clocked out'
          : endReason === 'official_work'
            ? 'Out on office work — still counting'
            : 'On a break — clock back in when you return'
      );
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not update attendance');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="att-clock att-clock-loading">Loading…</div>;

  // Banked minutes plus the session running right now. The server sends the
  // banked figure; adding the live run here avoids a request per second.
  const running = state.isClockedIn && state.openSince
    ? Math.max(0, (Date.now() - new Date(state.openSince).getTime()) / 60000)
    : 0;
  const banked = state.record?.workedMinutes || 0;
  const live = banked + running;
  const standard = state.record?.standardMinutes ?? state.standardMinutes;
  const pct = standard > 0 ? Math.min(100, (live / standard) * 100) : 100;
  const over = Math.max(0, live - standard);
  // A day that owes nothing (holiday / weekly off) has no bar to fill.
  const owesNothing = standard === 0;

  return (
    <div className={`att-clock${compact ? ' att-clock-compact' : ''}`}>
      <div className="att-clock-main">
        <div className="att-clock-figure">
          <span className="att-clock-time">{hhmm(live)}</span>
          <span className="att-clock-label">
            {owesNothing
              ? state.dayLabel || 'Non-working day'
              : `of ${hhmm(standard)} today`}
          </span>
        </div>

        {state.isClockedIn ? (
          // Clocking out asks WHY, because the answer decides whether the gap
          // that follows is paid time. A single "clock out" button would have
          // to guess, and it would guess wrong for every client visit.
          <div className="att-stepaway">
            <Button
              variant="secondary"
              loading={busy}
              disabled={busy}
              aria-expanded={stepAwayOpen}
              onClick={() => setStepAwayOpen((v) => !v)}
            >
              ■ Clock out
            </Button>
            {stepAwayOpen && (
              <ul className="att-stepaway-menu" role="menu">
                {STEP_AWAY.map((o) => (
                  <li key={o.reason}>
                    <button type="button" role="menuitem" onClick={() => clockOut(o.reason)}>
                      <span className="att-stepaway-label">{o.label}</span>
                      <span className="att-stepaway-hint">{o.hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <Button variant="primary" loading={busy} disabled={busy} onClick={clockIn}>
            ▶ Clock in
          </Button>
        )}
      </div>

      {!owesNothing && (
        <div className="att-bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
          <div className="att-bar-fill" style={{ width: `${pct}%` }} />
          {over > 0 && <div className="att-bar-over" />}
        </div>
      )}

      <div className="att-clock-meta">
        <span className={`att-dot${state.isClockedIn ? ' is-on' : ''}`} />
        {state.isClockedIn
          ? <>In since <b>{clockTime(state.openSince)}</b>
            {state.openWorkMode && (
              <span className={`att-mode-chip is-${state.openWorkMode}`}>
                {state.openWorkMode === 'office' ? (state.openOfficeName || 'Office') : 'Remote'}
              </span>
            )}
            {state.openDateKey && state.openDateKey !== state.dateKey && (
              <span className="att-warn"> · started {state.openDateKey}</span>
            )}</>
          : state.record?.lastOutAt
            ? <>Out since <b>{clockTime(state.record.lastOutAt)}</b></>
            : <>Not clocked in yet</>}
        {state.record?.officialWorkMinutes > 0 && (
          <span className="att-official-chip">
            incl. {hhmm(state.record.officialWorkMinutes)} out on office work
          </span>
        )}
        {over > 0 && <span className="att-over-chip">+{hhmm(over)} overtime</span>}
      </div>

      {/* Today's shape, as soon as there is one. The counter above says how
          much; this says how the day actually went — which is the only place
          a lunch break and a client visit look different. */}
      {!compact && state.record?.sessions?.length > 0 && (
        <div className="att-clock-timeline">
          <DayTimeline day={state.record} compact />
        </div>
      )}

      {/* Hidden behind a link rather than shown as a form: the normal path is
          the button above, and this is the exception. */}
      {!compact && state.allowSelfManualEntry && (
        <ManualHoursForm defaultDateKey={state.dateKey} onSaved={() => { onChange?.(); bump(); }} />
      )}
    </div>
  );
}

export default AttendanceClock;
export { hhmm, clockTime };
